/**
 * CodiceFiscaleField - Fase 9B.1
 *
 * Auto-calcola il CF quando cognome+nome+dataNascita+sesso+luogoNascita sono valorizzati.
 * Debounce 400ms. Mostra badge: "Calcolato automaticamente" / "Modificato manualmente" /
 * "CF non coerente".
 *
 * Props (controlled):
 *  - value (string)
 *  - onChange (string) => void
 *  - cognome, nome, dataNascita (YYYY-MM-DD), sesso (M|F), luogoNascita (string)
 *  - disabled (bool)
 *  - id, name, required, className
 *  - allowOverride (bool)  default true
 *  - onMetaChange (meta) => void  (opzionale; meta = { computed, manual, valid, errore })
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEBOUNCE_MS = 400;

function allInputsReady({ cognome, nome, dataNascita, sesso, luogoNascita }) {
  return (
    !!(cognome && cognome.trim().length >= 2) &&
    !!(nome && nome.trim().length >= 2) &&
    !!(dataNascita && /^\d{4}-\d{2}-\d{2}$/.test(dataNascita)) &&
    !!(sesso && ["M", "F", "m", "f"].includes(sesso)) &&
    !!(luogoNascita && luogoNascita.trim().length >= 2)
  );
}

export default function CodiceFiscaleField({
  value = "",
  onChange,
  cognome,
  nome,
  dataNascita,
  sesso,
  luogoNascita,
  disabled = false,
  id = "codice-fiscale",
  name = "codice_fiscale",
  required = false,
  className = "",
  allowOverride = true,
  onMetaChange,
}) {
  const [computed, setComputed] = useState("");
  const [manual, setManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState(null);
  const [valid, setValid] = useState(null); // null|true|false
  const debounceRef = useRef(null);
  const lastInputsRef = useRef("");

  // Notify parent meta
  useEffect(() => {
    if (typeof onMetaChange === "function") {
      onMetaChange({ computed, manual, valid, errore });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed, manual, valid, errore]);

  // Auto-calcolo on relevant inputs change
  useEffect(() => {
    const ready = allInputsReady({ cognome, nome, dataNascita, sesso, luogoNascita });
    const inputsKey = JSON.stringify({ cognome, nome, dataNascita, sesso, luogoNascita });
    if (!ready) return;
    if (inputsKey === lastInputsRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      lastInputsRef.current = inputsKey;
      setLoading(true);
      setErrore(null);
      try {
        const resp = await axios.post(`${BACKEND}/api/utils/codice-fiscale/calcola`, {
          cognome,
          nome,
          data_nascita: dataNascita,
          sesso: String(sesso).toUpperCase(),
          luogo_nascita: luogoNascita,
        });
        const cfNew = resp.data?.cf || "";
        setComputed(cfNew);
        // Se l'utente non ha modificato manualmente, propaga
        const currentUpper = (value || "").trim().toUpperCase();
        if (!manual || !currentUpper || currentUpper === computed) {
          if (typeof onChange === "function") onChange(cfNew);
          setValid(true);
          setManual(false);
        } else {
          // C'è un override manuale: confronta
          setValid(currentUpper === cfNew);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || "Errore calcolo CF";
        setErrore(typeof detail === "string" ? detail : "Errore calcolo CF");
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognome, nome, dataNascita, sesso, luogoNascita]);

  // Validazione del valore corrente vs computed
  useEffect(() => {
    const v = (value || "").trim().toUpperCase();
    if (!v) {
      setValid(null);
      return;
    }
    if (computed) {
      setValid(v === computed);
    }
  }, [value, computed]);

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase();
    if (typeof onChange === "function") onChange(v);
    if (computed && v !== computed) {
      setManual(true);
    } else if (computed && v === computed) {
      setManual(false);
    }
  };

  const handleResetToComputed = () => {
    if (!computed) return;
    if (typeof onChange === "function") onChange(computed);
    setManual(false);
    setValid(true);
  };

  const renderBadge = () => {
    if (loading) {
      return <span className="text-xs text-gray-500">Calcolo in corso…</span>;
    }
    if (errore) {
      return <span className="text-xs text-orange-600">⚠ {errore} — puoi inserirlo manualmente</span>;
    }
    if (!value) {
      return <span className="text-xs text-gray-400">Compila i dati anagrafici per il calcolo automatico</span>;
    }
    if (manual && computed && value.toUpperCase() !== computed) {
      return (
        <span className="text-xs text-orange-600 inline-flex items-center gap-2">
          ⚠ Modificato manualmente — atteso <code className="bg-orange-50 px-1 rounded">{computed}</code>
          {allowOverride && (
            <button type="button" onClick={handleResetToComputed} className="underline text-blue-600">
              Ripristina calcolato
            </button>
          )}
        </span>
      );
    }
    if (computed && value.toUpperCase() === computed) {
      return <span className="text-xs text-green-600">✓ Calcolato automaticamente</span>;
    }
    if (valid === false) {
      return <span className="text-xs text-red-600">✗ CF non coerente con i dati anagrafici</span>;
    }
    return null;
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <input
        type="text"
        id={id}
        name={name}
        value={value || ""}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        maxLength={16}
        placeholder="Codice Fiscale (16 caratteri)"
        autoComplete="off"
        className="w-full px-3 py-2 font-mono uppercase tracking-wider border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        data-testid="codice-fiscale-input"
      />
      <div className="min-h-[18px]" data-testid="codice-fiscale-badge">{renderBadge()}</div>
    </div>
  );
}
